/**
 * Cart Drawer Edit Item Overlay
 *
 * Bottom-sheet dialog for changing variant options on items already in cart.
 * Removes the old line item (via /cart/change.js quantity=0) and adds the new
 * variant (via /cart/add.js). Follows the same HTMLElement + fetch + CustomEvent
 * pattern as CartDrawerFreeGiftModal.
 */


import { announceCartLinesUpdate } from '@theme/cart-events';
class CartDrawerEditItem extends HTMLElement {
  /** @type {HTMLDialogElement|null} */
  #dialog = null;

  /** @type {Object|null} */
  #productData = null;

  /** @type {string|null} line item key of the item being edited */
  #lineKey = null;

  /** @type {number} quantity of the original line item */
  #lineQuantity = 1;

  /** @type {Object} selected option values keyed by option name */
  #selectedOptions = {};

  /** @type {number|null} */
  #selectedVariantId = null;

  /** @type {number|null} original variant ID for comparison */
  #originalVariantId = null;

  /** @type {boolean} whether the line carries properties (e.g. a gift card's recipient) */
  #hasProperties = false;

  /** @type {boolean} */
  #isUpdating = false;

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');

    if (!this.#dialog) {
      return;
    }

    this.#dialog.addEventListener('click', this.#handleDialogClick.bind(this));
    this.#dialog.addEventListener('cancel', this.#handleCancel.bind(this));

    const closeBtn = this.querySelector('[ref="closeBtn"]');

    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.close());
    }

    const ctaBtn = this.querySelector('[ref="ctaBtn"]');

    if (ctaBtn) {
      ctaBtn.addEventListener('click', () => this.#updateItem());
    }
  }

  /**
   * Open the overlay and populate it with product + line item data.
   * @param {Object} params
   * @param {string} params.lineKey - cart line item key
   * @param {number} params.lineQuantity - current quantity
   * @param {number} params.variantId - current variant ID
   * @param {Object} params.productData - same shape as quick-add productData
   */
  open(params) {
    if (!this.#dialog) {
      return;
    }

    this.#lineKey = params.lineKey;
    this.#lineQuantity = params.lineQuantity || 1;
    this.#originalVariantId = params.variantId;
    this.#hasProperties = Boolean(params.hasProperties);
    this.#productData = params.productData;
    this.#selectedOptions = {};
    this.#selectedVariantId = null;

    this.#populateHeader();
    this.#populateOptions();
    this.#selectCurrentVariant();

    this.#dialog.showModal();
  }

  close() {
    if (!this.#dialog || !this.#dialog.open) {
      return;
    }

    this.#dialog.classList.add('dialog-closing');

    const handleAnimationEnd = () => {
      this.#dialog.classList.remove('dialog-closing');
      this.#dialog.close();
      this.#dialog.removeEventListener('animationend', handleAnimationEnd);
    };

    this.#dialog.addEventListener('animationend', handleAnimationEnd);
  }

  /**
   * Closes the dialog immediately, with no slide-down animation.
   *
   * Used on the success path of #updateItem: the animated close() defers the actual
   * `dialog.close()` to an `animationend` event, and if the cart re-render lands in between the
   * shopper is left looking at a half-torn-down panel. Also resets the thumbnail so a stale image
   * can never flash when the panel is reopened for a different line item.
   */
  #closeNow() {
    if (!this.#dialog) {
      return;
    }

    this.#dialog.classList.remove('dialog-closing');

    if (this.#dialog.open) {
      this.#dialog.close();
    }

    const imgEl = this.querySelector('[ref="productImage"]');

    if (imgEl) {
      imgEl.removeAttribute('src');
      imgEl.alt = '';
      imgEl.hidden = true;
    }
  }

  #populateHeader() {
    const imgEl = this.querySelector('[ref="productImage"]');
    const titleEl = this.querySelector('[ref="productTitle"]');
    const priceEl = this.querySelector('[ref="productPrice"]');

    if (imgEl && this.#productData.featuredImage) {
      imgEl.src = this.#productData.featuredImage;
      imgEl.alt = this.#productData.title || '';
      imgEl.hidden = false;
    } else if (imgEl) {
      // No image: hide the element rather than setting src='' (which renders a broken-image icon).
      imgEl.removeAttribute('src');
      imgEl.alt = '';
      imgEl.hidden = true;
    }

    if (titleEl) {
      titleEl.textContent = this.#productData.title || '';
    }

    if (priceEl) {
      priceEl.textContent = this.#productData.price || '';
    }
  }

  #populateOptions() {
    const colorSection = this.querySelector('[ref="colorSection"]');
    const sizeSection = this.querySelector('[ref="sizeSection"]');
    const swatchesWrap = this.querySelector('[ref="swatchesWrap"]');
    const sizesWrap = this.querySelector('[ref="sizesWrap"]');
    const colorLabel = this.querySelector('[ref="colorLabel"]');
    const sizeLabel = this.querySelector('[ref="sizeLabel"]');

    if (colorSection) {
      colorSection.style.display = 'none';
    }

    if (sizeSection) {
      sizeSection.style.display = 'none';
    }

    if (swatchesWrap) {
      swatchesWrap.innerHTML = '';
    }

    if (sizesWrap) {
      sizesWrap.innerHTML = '';
    }

    if (!this.#productData.options || this.#productData.options.length === 0) {
      return;
    }

    // Exactly ONE colour option and ONE size option. `#isColorOption` matches 'color', 'colour'
    // AND 'style', and the two containers are cleared once above the loop — so a product carrying
    // both a Color and a Style option appended both value sets into the same swatch grid,
    // doubling the disc count while the label described only whichever came last. The mirror-image
    // collision existed for any two non-colour options, which both emptied into `sizesWrap`.
    const colorOptionName = this.#productData.options.find((option) => this.#isColorOption(option.name))?.name;
    let sizeRendered = false;

    for (const option of this.#productData.options) {
      const isColor = option.name === colorOptionName;

      if (!isColor) {
        if (sizeRendered) continue;
        sizeRendered = true;
      }

      if (isColor && colorSection && swatchesWrap) {
        if (colorLabel) {
          colorLabel.innerHTML = `${option.name}: <strong ref="colorValue"></strong>`;
        }

        for (const value of option.values) {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'cart-drawer-edit-item__swatch';
          btn.dataset.optionName = option.name;
          btn.dataset.optionValue = value;
          btn.setAttribute('aria-label', value);
          btn.title = value;
          btn.style.setProperty('--swatch-bg', this.#getSwatchBackground(value));

          btn.addEventListener('click', () => {
            this.#selectOption(option.name, value);
          });

          swatchesWrap.appendChild(btn);
        }

        colorSection.style.display = '';
      } else if (sizeSection && sizesWrap) {
        if (sizeLabel) {
          sizeLabel.innerHTML = `${option.name}: <strong ref="sizeValue"></strong>`;
        }

        for (const value of option.values) {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'cart-drawer-edit-item__size';
          btn.dataset.optionName = option.name;
          btn.dataset.optionValue = value;
          btn.textContent = value;

          btn.addEventListener('click', () => {
            this.#selectOption(option.name, value);
          });

          sizesWrap.appendChild(btn);
        }

        sizeSection.style.display = '';
      }
    }
  }

  /**
   * Select the current variant's options (the variant already in cart).
   */
  #selectCurrentVariant() {
    if (!this.#productData || !this.#productData.variants || !this.#originalVariantId) {
      return;
    }

    const currentVariant = this.#productData.variants.find(
      (v) => v.id === this.#originalVariantId
    );

    if (!currentVariant) {
      this.#selectFirstAvailable();
      return;
    }

    for (const [optionName, value] of Object.entries(currentVariant.options)) {
      this.#selectedOptions[optionName] = value;
    }

    this.#updateUI();
  }

  #selectFirstAvailable() {
    if (!this.#productData || !this.#productData.variants) {
      return;
    }

    const firstAvailable = this.#productData.variants.find((v) => v.available);

    if (!firstAvailable) {
      return;
    }

    for (const [optionName, value] of Object.entries(firstAvailable.options)) {
      this.#selectedOptions[optionName] = value;
    }

    this.#updateUI();
  }

  /**
   * @param {string} optionName
   * @param {string} value
   */
  #selectOption(optionName, value) {
    this.#selectedOptions[optionName] = value;

    // A sold-out colour keeps the chosen size; the CTA reads "Sold out" until the shopper changes it.
    this.#updateUI();
  }

  #updateUI() {
    this.#updateVariant();
    this.#updateSwatchSelection();
    this.#updateSizeSelection();
    this.#updateProductImage();
    this.#updateOptionLabels();
    this.#updatePrice();
    this.#updateCta();
  }

  #updateVariant() {
    if (!this.#productData || !this.#productData.variants) {
      this.#selectedVariantId = null;
      return;
    }

    const match = this.#productData.variants.find((v) => {
      for (const [key, val] of Object.entries(this.#selectedOptions)) {
        if (v.options[key] !== val) {
          return false;
        }
      }

      return true;
    });

    if (match && match.available) {
      this.#selectedVariantId = match.id;
    } else {
      this.#selectedVariantId = null;
    }
  }

  #updateSwatchSelection() {
    const swatches = this.querySelectorAll('.cart-drawer-edit-item__swatch');

    for (const swatch of swatches) {
      const optionName = swatch.dataset.optionName;
      const optionValue = swatch.dataset.optionValue;
      const isSelected = this.#selectedOptions[optionName] === optionValue;

      swatch.classList.toggle('cart-drawer-edit-item__swatch--selected', isSelected);

      const isAvailable = this.#isOptionValueAvailable(optionName, optionValue);
      swatch.classList.toggle('cart-drawer-edit-item__swatch--unavailable', !isAvailable);
    }
  }

  #updateSizeSelection() {
    const sizes = this.querySelectorAll('.cart-drawer-edit-item__size');

    for (const size of sizes) {
      const optionName = size.dataset.optionName;
      const optionValue = size.dataset.optionValue;
      const isSelected = this.#selectedOptions[optionName] === optionValue;

      size.classList.toggle('cart-drawer-edit-item__size--selected', isSelected);

      const isAvailable = this.#isOptionValueAvailable(optionName, optionValue);
      size.classList.toggle('cart-drawer-edit-item__size--unavailable', !isAvailable);
    }
  }

  #updateProductImage() {
    const imgEl = this.querySelector('[ref="productImage"]');

    if (!imgEl || !this.#productData) {
      return;
    }

    const colorOption = this.#productData.options.find((o) => this.#isColorOption(o.name));

    if (!colorOption) {
      return;
    }

    const selectedColor = this.#selectedOptions[colorOption.name];

    if (!selectedColor) {
      return;
    }

    const matchingVariant = this.#productData.variants.find(
      (v) => v.options[colorOption.name] === selectedColor && v.image
    );

    const nextSrc = matchingVariant?.image || this.#productData.featuredImage;

    if (!nextSrc) {
      return;
    }

    imgEl.src = nextSrc;
    // #populateHeader hides the thumbnail when the product JSON carried no featured image; a
    // variant image resolved here has to un-hide it, or the panel shows a gap where the photo
    // should be.
    imgEl.alt = this.#productData.title || '';
    imgEl.hidden = false;
  }

  #updateOptionLabels() {
    for (const option of this.#productData?.options || []) {
      const isColor = this.#isColorOption(option.name);
      const refKey = isColor ? 'colorValue' : 'sizeValue';
      const el = this.querySelector(`[ref="${refKey}"]`);

      if (el) {
        el.textContent = this.#selectedOptions[option.name] || '';
      }
    }
  }

  #updatePrice() {
    const priceEl = this.querySelector('[ref="productPrice"]');

    if (!priceEl || !this.#productData) {
      return;
    }

    const match = this.#productData.variants.find((v) => {
      for (const [key, val] of Object.entries(this.#selectedOptions)) {
        if (v.options[key] !== val) {
          return false;
        }
      }

      return true;
    });

    if (match && match.price) {
      priceEl.textContent = match.price;
    } else {
      priceEl.textContent = this.#productData.price || '';
    }
  }

  #updateCta() {
    const ctaBtn = this.querySelector('[ref="ctaBtn"]');

    if (!ctaBtn) {
      return;
    }

    // Disable if no variant selected, if currently updating, or if same variant
    const isSameVariant = this.#selectedVariantId === this.#originalVariantId;
    ctaBtn.disabled = !this.#selectedVariantId || this.#isUpdating || isSameVariant;

    // Live labels the CTA "Update" (disabled until a different variant is chosen); an unbuyable combination reads "Sold out".
    ctaBtn.textContent = this.#selectedVariantId ? 'Update' : 'Sold out';
  }

  /**
   * @param {string} optionName
   * @param {string} optionValue
   * @returns {boolean}
   */
  #isOptionValueAvailable(optionName, optionValue) {
    if (!this.#productData || !this.#productData.variants) {
      return false;
    }

    // Sizes follow Shopify's earlier-options rule; colours also respect the selected size, as the PDP does.
    // Mirrors assets/cart-drawer-quick-add.js — keep the two in sync.
    const optionOrder = (this.#productData.options || []).map((option) => option.name);
    const optionIndex = optionOrder.indexOf(optionName);
    const constrainingOptions = this.#isColorOption(optionName)
      ? optionOrder.filter((name) => name !== optionName)
      : optionOrder.slice(0, Math.max(optionIndex, 0));

    return this.#productData.variants.some((v) => {
      if (!v.available) {
        return false;
      }

      if (v.options[optionName] !== optionValue) {
        return false;
      }

      return constrainingOptions.every((name) => {
        const selected = this.#selectedOptions[name];
        return selected === undefined || v.options[name] === selected;
      });
    });
  }

  /**
   * @param {string} name
   * @returns {boolean}
   */
  #isColorOption(name) {
    const lower = name.toLowerCase();
    return lower === 'color' || lower === 'colour' || lower === 'style';
  }

  /**
   * Resolves a colour value to a CSS `background` shorthand.
   *
   * `snippets/cart-products.liquid` builds the map with the same three-tier resolution the card
   * swatches use, so a value can be a hex, a `linear-gradient(...)` or a
   * `url(…) center/cover no-repeat` — all valid `background` shorthands. The older
   * `{color, image}` object shape is still accepted so a stale cached document can't break.
   *
   * @param {string} colorValue
   * @returns {string}
   */
  #getSwatchBackground(colorValue) {
    const info = this.#lookupSwatch(colorValue);

    if (typeof info === 'string' && info) return info;
    if (info?.image) return `url('${info.image}') center/cover no-repeat`;
    if (info?.color) return info.color;

    // Unresolved colours paint with the colour NAME as a CSS colour — "Black", "Navy", "White" and
    // friends are valid keywords — and only then a neutral grey. The old unconditional #f3f3f3 was
    // near-invisible on the panel's white background, so every miss read as a blank disc. Matches
    // the live site (goat-shopify-redesign utils: `return { color_code: colorName }`).
    //
    // #ccc, not #eee: that is what `snippets/swatch.liquid` paints for an unresolved swatch when the
    // caller passes `shop_color_lookup: true`, which the PDP picker does. Three different greys for
    // one state (#ccc on the PDP, #eee here, #f3f3f3 in the CSS var fallback) is the same drift that
    // put this overlay's palette out of step with the PDP in the first place.
    const name = String(colorValue || '').trim();

    return name && CSS.supports('color', name) ? name : '#ccc';
  }

  /**
   * Reads the Liquid-built swatch map, retrying case- and whitespace-insensitively.
   *
   * The map is keyed by `option_value.name` from Liquid, but the buttons are built from
   * `/products/<handle>.js`, whose option values come from the variants. A colour saved as `"Bone "`
   * in the admin therefore produces a key and a lookup that differ by a character nobody can see,
   * and the disc silently falls through to the grey placeholder.
   *
   * @param {string} colorValue
   * @returns {string | {color?: string, image?: string} | undefined}
   */
  #lookupSwatch(colorValue) {
    const map = this.#productData?.swatchMap;

    if (!map) return undefined;
    if (map[colorValue] != null) return map[colorValue];

    const wanted = String(colorValue || '')
      .trim()
      .toLowerCase();

    if (!wanted) return undefined;

    const key = Object.keys(map).find((candidate) => candidate.trim().toLowerCase() === wanted);

    return key === undefined ? undefined : map[key];
  }

  /**
   * Swap the line to the selected variant at the same quantity, keeping any line properties.
   */
  async #updateItem() {
    if (!this.#selectedVariantId || this.#isUpdating || !this.#lineKey) {
      return;
    }

    if (this.#selectedVariantId === this.#originalVariantId) {
      this.close();
      return;
    }

    this.#isUpdating = true;
    const ctaBtn = this.querySelector('[ref="ctaBtn"]');
    const originalText = ctaBtn ? ctaBtn.textContent : '';

    if (ctaBtn) {
      ctaBtn.classList.add('is-loading');
      ctaBtn.textContent = 'Updating...';
      ctaBtn.disabled = true;
    }

    try {
      // ONE atomic call. This used to be `/cart/change.js` quantity=0 followed by
      // `/cart/add.js`, which destroys the shopper's line BEFORE the replacement is known to be
      // addable — if the add lost a stock race the line was silently gone and the catch below only
      // flashed "Error", leaving the drawer rendering a line the cart no longer had.
      // `/cart/change.js` has no parameter that swaps the variant on a line (its `id`/`line` picks
      // the line; only quantity/properties/selling_plan are settable), so `/cart/update.js` with
      // both keys in one payload is the only atomic option — and it is what the parity source
      // does (goat-shopify-redesign cart-product-overlay.jsx: `{[newId]: 1, [oldId]: 0}`).
      //
      // Caveat this inherits from that approach: `/cart/update.js` keys by VARIANT, so it cannot
      // distinguish two lines of the same variant carrying different line properties. Editing one
      // of those would clear both. Acceptable here because the Edit affordance is only rendered
      // for ordinary variant lines, and it is strictly safer than the previous ordering.
      const routes = window.Theme?.routes ?? {};
      const sections = this.#getSectionIds();
      const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
      // `sections_url` matters for the drawer: its render target
      // (sections/cart-drawer-section.liquid → snippets/cart-drawer.liquid) is gated on
      // `template.name != 'cart'`, so the markup depends on the render context.
      const sectionParams = sections.length ? { sections: sections.join(','), sections_url: window.location.pathname } : {};
      const properties = this.#hasProperties ? await this.#readLineProperties() : null;

      let response;
      if (properties) {
        // `/cart/update.js` cannot carry line properties (a gift card's recipient and note), so add the
        // new variant with them first, then drop the old line by key: a failed add loses nothing.
        const added = await fetch(routes.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers,
          body: JSON.stringify({ items: [{ id: this.#selectedVariantId, quantity: this.#lineQuantity, properties }] }),
        });

        if (!added.ok) {
          const errorData = await added.json().catch(() => ({}));
          throw new Error(errorData.description || errorData.message || 'Failed to update item');
        }

        response = await fetch(`${routes.cart_change_url || '/cart/change'}.js`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ id: this.#lineKey, quantity: 0, ...sectionParams }),
        });

        if (!response.ok) {
          await announceCartLinesUpdate({ action: 'add', source: 'cart-drawer-edit-item' });
        }
      } else {
        response = await fetch(routes.cart_update_url || '/cart/update', {
          method: 'POST',
          headers,
          body: JSON.stringify({
            updates: {
              [this.#originalVariantId]: 0,
              [this.#selectedVariantId]: this.#lineQuantity,
            },
            ...sectionParams,
          }),
        });
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.description || errorData.message || 'Failed to update item');
      }

      const data = await response.json();

      // Close synchronously (not the animated close) BEFORE the cart re-renders below. The
      // animated close() only calls `dialog.close()` on `animationend`, so the panel would still
      // be open when the cart update lands.
      this.#closeNow();

      await announceCartLinesUpdate({
        action: 'update',
        lines: [{ merchandiseId: String(this.#selectedVariantId), quantity: this.#lineQuantity }],
        source: 'cart-drawer-edit-item',
        sourceId: 'cart-drawer-edit-item',
        sections: data.sections,
        variantId: String(this.#selectedVariantId),
        // `/cart/update.js` responds with the whole cart, so the announcement needs no extra read.
        cart: data,
      });
    } catch (error) {
      console.error('[cart-drawer-edit-item]', error.message);

      if (ctaBtn) {
        ctaBtn.textContent = 'Error';
        setTimeout(() => {
          ctaBtn.textContent = originalText;
          ctaBtn.disabled = false;
        }, 1500);
      }
    } finally {
      this.#isUpdating = false;

      if (ctaBtn) {
        ctaBtn.classList.remove('is-loading');
      }
    }
  }

  /**
   * The edited line's properties, read fresh from the cart so hidden `_` keys stay out of the markup.
   * @returns {Promise<Record<string, string> | null>}
   */
  async #readLineProperties() {
    const response = await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('Failed to read cart');

    const cart = await response.json();
    const line = cart.items?.find((item) => item.key === this.#lineKey);
    const properties = Object.fromEntries(Object.entries(line?.properties ?? {}).filter(([, value]) => value != null));
    return Object.keys(properties).length ? properties : null;
  }

  /**
   * Every cart line-items section on the page, so the drawer AND the /cart page stay in sync.
   * Only the first was collected before, which left the other rendering a stale cart.
   * Mirrors `assets/component-cart-items.js`.
   *
   * @returns {Array<string>}
   */
  #getSectionIds() {
    const ids = new Set();

    for (const element of document.querySelectorAll('cart-items-component[data-section-id]')) {
      const id = /** @type {HTMLElement} */ (element).dataset.sectionId;
      if (id) ids.add(id);
    }

    return Array.from(ids);
  }

  /**
   * @param {MouseEvent} event
   */
  #handleDialogClick(event) {
    if (event.target === this.#dialog) {
      this.close();
    }
  }

  /**
   * @param {Event} event
   */
  #handleCancel(event) {
    event.preventDefault();
    this.close();
  }
}

if (!customElements.get('cart-drawer-edit-item')) {
  customElements.define('cart-drawer-edit-item', CartDrawerEditItem);
}
